
import { useContext, useEffect } from 'react';
import { WebSocketContext } from '../contexts/WebSocketContext';
import type { WebSocketEventType, WebSocketEventHandler, WebSocketMessage } from '../types/websocket.types';

export function useWebSocket() {
  const context = useContext(WebSocketContext);

  if (!context) {
    throw new Error('useWebSocket must be used within WebSocketProvider');
  }

  return context;
}

export function useWebSocketEvent<T = unknown>(
  eventType: WebSocketEventType,
  handler: WebSocketEventHandler<T>,
  deps: React.DependencyList = []
) {
  const { service } = useWebSocket();

  useEffect(() => {
    if (!service) return;

    const unsubscribe = service.on(
      eventType,
      handler as (payload: unknown, message: WebSocketMessage<unknown>) => void
    );

    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service, eventType, ...deps]);
}
export function useWebSocketChannel(channel: string) {
  const { service, isConnected } = useWebSocket();

  useEffect(() => {
    if (!service || !isConnected || !channel) return;
    service.send({
      type: 'SUBSCRIBE' as WebSocketEventType,
      payload: { channels: [channel] },
      timestamp: new Date().toISOString(),
    });

    if (import.meta.env.DEV) {
      console.warn(`📢 Subscribed to channel: ${channel}`);
    }

    return () => {
      if (service.isConnected()) {
        service.send({
          type: 'UNSUBSCRIBE' as WebSocketEventType,
          payload: { channels: [channel] },
          timestamp: new Date().toISOString(),
        });

        if (import.meta.env.DEV) {
          console.warn(`🔇 Unsubscribed from channel: ${channel}`);
        }
      }
    };
  }, [service, isConnected, channel]);
}


